import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';

import ServiceBg from '../Assets/Images/ServiceBg.jpg';
import IntegrityImage from '../Assets/Images/Integrity.jpg';
import ExcellenceImage from '../Assets/Images/Excellence-image.jpeg';
import CustomerCentricityImage from '../Assets/Images/CustomerCentricityImage.jpg';
import SustainabilityImage from '../Assets/Images/SustainabilityImage.jpg';

const Services = () => {
  const [currentValueIndex, setCurrentValueIndex] = useState(0);

  const values = [
    {
      title: 'Integrity',
      description: (
        <>
          <p className="text-sm font-medium text-black">
            We conduct our business with the highest standards of honesty, ethics, and transparency.
            Integrity is the foundation of everything we do, guiding our decisions and actions at every level of the organization.
          </p>
          <p className="text-sm font-medium text-black mt-1">
            We believe that long-term success is built on trust—trust earned through consistency, accountability, and ethical behavior.
            Whether we are engaging with clients, collaborating with partners, or communicating with stakeholders, we are committed to doing the right thing, even when it is not the easiest path.
          </p>
        </>
      ),
      image: IntegrityImage,
      color: 'text-[#70962f]',
    },
    {
      title: 'Excellence',
      description: (
        <>
          <p className="text-sm font-medium text-black">
            We are committed to delivering top-quality solutions and services, grounded in a relentless pursuit of excellence.
            In the rapidly evolving renewable energy sector, we recognize that excellence is not a destination but an ongoing journey fueled by continuous improvement, innovation, and a passion for achieving the highest standards.
          </p>
          <p className="text-sm font-medium text-black mt-1">
            Our team strives to exceed expectations in every project we undertake.
            From initial consultation to final implementation, we approach each task with precision, care, and a focus on long-term value.
          </p>
        </>
      ),
      image: ExcellenceImage,
      color: 'text-blue-800',
    },
    {
      title: 'Customer-Centricity',
      description: (
        <>
          <p className="text-sm font-medium text-black">
            Our clients are at the heart of everything we do. We listen attentively to their needs, understand their goals, and work collaboratively to develop tailored solutions that exceed expectations.
          </p>
          <p className="text-sm font-medium text-black mt-1">
            Feedback from our clients is a vital part of our improvement process. We actively seek their input and use it to refine our strategies, enhance our services, and evolve with their changing needs.
          </p>
        </>
      ),
      image: CustomerCentricityImage,
      color: 'text-yellow-800',
    },
    {
      title: 'Sustainability',
      description: (
        <>
          <p className="text-sm font-medium text-black">
            We prioritize environmental responsibility in all our operations, embedding sustainability into the core of our mission and daily practices.
            As a company operating in the renewable energy sector, we understand the urgent need to transition toward cleaner, more sustainable sources of power.
          </p>
          <p className="text-sm font-medium text-black mt-1">
            Our commitment to sustainability goes beyond delivering green energy solutions. It encompasses how we design our systems, source our materials, manage our resources, and engage with communities.
          </p>
        </>
      ),
      image: SustainabilityImage,
      color: 'text-red-800',
    },
  ];

  const handlePrevSlide = () => {
    setCurrentValueIndex((prevIndex) => (prevIndex - 1 + values.length) % values.length);
  };

  const handleNextSlide = () => {
    setCurrentValueIndex((prevIndex) => (prevIndex + 1) % values.length);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentValueIndex((prevIndex) => (prevIndex + 1) % values.length);
    }, 5000); // Changed to 5s for better UX

    return () => clearInterval(interval);
  }, [values.length]);

  return (
    <section id="services" className="relative py-16 bg-center bg-no-repeat">
      <div
        className="absolute inset-0 bg-cover opacity-30 z-0"
        style={{ backgroundImage: `url(${ServiceBg})` }}
      ></div>

      <div className="relative z-10 container mx-auto px-4">
        <h2 className="text-3xl font-bold text-[#587820] text-center mb-12">
          Our Values at "SolarMore Solutions"
        </h2>

        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${currentValueIndex * 100}%)` }}
          >
            {values.map((value, index) => (
              <div key={index} className="min-w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center justify-center">
                    <div className={`${value.bgColor} p-2 rounded-xl`}>
                      <img
                        src={value.image}
                        alt={value.title}
                        className="w-[500px] h-[350px] rounded-xl object-cover"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col justify-center px-4">
                    <h3 className={`text-xl font-semibold ${value.color} mb-2`}>{value.title}</h3>
                    {value.description}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Left & Right Arrows */}
          <button
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-[#70962f] text-white p-3 rounded-full hover:bg-green-800 transition-colors"
            onClick={handlePrevSlide}
            aria-label="Previous slide"
          >
            <FaArrowLeft />
          </button>
          <button
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#70962f] text-white p-3 rounded-full hover:bg-green-800 transition-colors"
            onClick={handleNextSlide}
            aria-label="Next slide"
          >
            <FaArrowRight />
          </button>

          {/* Slide Indicators */}
          <div className="flex justify-center mt-6 space-x-2">
            {values.map((_, index) => (
              <button
                key={index}
                className={`w-3 h-3 rounded-full ${
                  currentValueIndex === index ? 'bg-[#70962f]' : 'bg-gray-300'
                }`}
                onClick={() => setCurrentValueIndex(index)}
              />
            ))}
          </div>
        </div>

        {/* Navigation Links */}
        <div className="flex justify-center gap-4 mt-8">
          <Link
            to="/ourValues"
            className="inline-flex items-center gap-2 bg-[#70962f] text-white px-6 py-3 rounded-full hover:bg-green-800 transition-colors duration-300"
          >
            Our Values <FaArrowRight />
          </Link>
          <Link
            to="/ourOffering"
            className="inline-flex items-center gap-2 bg-[#70962f] text-white px-6 py-3 rounded-full hover:bg-green-800 transition-colors duration-300"
          >
            Our Offering <FaArrowRight />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Services;
